import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Alert, CircularProgress, Autocomplete,
    InputAdornment, Typography, Paper, Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GavelIcon from '@mui/icons-material/Gavel';
import { getCombosJWT, crearResolucionJWT } from '../../api/endpoints';
import { capitalizar } from '../../utils/capitalizar';

function NuevaResolucionDialog({ open, onClose, idExpediente, onCreada }) {
    const [form, setForm] = useState({
        numeroAnio: '',
        nroExpediente: '',
        fecha: '',
        descripcion: '',
        monto: '',
    });
    const [reparticiones, setReparticiones] = useState([]);
    const [reparticionSeleccionada, setReparticionSeleccionada] = useState(null);
    const [archivo, setArchivo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dragActive, setDragActive] = useState(false);

    // Acepta un File si es PDF; sino setea error y lo descarta.
    const tomarArchivo = (file) => {
        if (!file) return;
        const esPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
        if (!esPdf) { setError('El archivo debe ser un PDF'); return; }
        setError('');
        setArchivo(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        tomarArchivo(e.dataTransfer?.files?.[0]);
    };

    useEffect(() => {
        if (open) {
            getCombosJWT('reparticiones_todas', '', 500).then(resp => {
                if (resp?.ok) {
                    const items = (resp.items || []).filter(r => (r.label || '').trim());
                    setReparticiones(items);
                }
            }).catch(() => {});
        }
    }, [open]);

    const handleClose = () => {
        setForm({ numeroAnio: '', nroExpediente: '', fecha: '', descripcion: '', monto: '' });
        setReparticionSeleccionada(null);
        setArchivo(null);
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!form.numeroAnio.trim()) return setError('Nº Resolución es obligatorio');
        if (!form.nroExpediente.trim()) return setError('Nº de Expediente es obligatorio');
        if (!form.descripcion.trim()) return setError('Descripción es obligatoria');
        if (!reparticionSeleccionada) return setError('Origen (Repartición) es obligatorio');

        setLoading(true);
        setError('');

        try {
            const fd = new FormData();
            fd.append('numeroAnio', form.numeroAnio.trim());
            fd.append('nroExpediente', form.nroExpediente.trim());
            if (form.fecha) fd.append('fecha', form.fecha);
            fd.append('descripcion', form.descripcion.trim());
            fd.append('idReparticion', String(reparticionSeleccionada.id));
            if (form.monto) fd.append('monto', form.monto);
            if (archivo) fd.append('archivo', archivo);

            const resp = await crearResolucionJWT(idExpediente, fd);
            if (resp?.ok) {
                handleClose();
                onCreada?.();
            } else {
                setError(resp?.mensaje || 'Error al crear resolución');
            }
        } catch (err) {
            setError('Error al crear resolución');
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1048576).toFixed(1)} MB`;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                pb: 1.5,
            }}>
                <GavelIcon />
                Nueva Resolución
            </DialogTitle>

            <DialogContent sx={{ pt: '20px !important', pb: 1 }}>
                <Box display="flex" flexDirection="column" gap={2.5}>
                    {error && <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 1 }}>{error}</Alert>}

                    <Box display="flex" gap={2}>
                        <TextField
                            label="Nº Resolución"
                            value={form.numeroAnio}
                            onChange={e => setForm(f => ({ ...f, numeroAnio: e.target.value }))}
                            placeholder="Ej: 123/FE/2026"
                            required
                            fullWidth
                            size="small"
                            autoFocus
                        />
                        <TextField
                            label="Nº de Expediente"
                            value={form.nroExpediente}
                            onChange={e => setForm(f => ({ ...f, nroExpediente: e.target.value }))}
                            required
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Fecha"
                            type="date"
                            value={form.fecha}
                            onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
                            fullWidth
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Box>

                    <TextField
                        label="Descripción"
                        value={form.descripcion}
                        onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                        required
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                    />

                    <Box display="flex" gap={2}>
                        <Autocomplete
                            options={reparticiones}
                            getOptionLabel={opt => capitalizar(opt.label || '')}
                            value={reparticionSeleccionada}
                            onChange={(_, v) => setReparticionSeleccionada(v)}
                            renderOption={(props, opt) => (
                                <li {...props} key={opt.id}>
                                    <Typography variant="body2">{capitalizar(opt.label || '')}</Typography>
                                </li>
                            )}
                            renderInput={params => <TextField {...params} label="Origen (Repartición)" size="small" required />}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            size="small"
                            sx={{ flex: 3 }}
                            slotProps={{
                                listbox: { sx: { maxHeight: 250, '& .MuiAutocomplete-option': { py: 0.75, px: 1.5, borderBottom: '1px solid #f0f0f0' } } }
                            }}
                        />
                        <TextField
                            label="Monto"
                            value={form.monto}
                            onChange={e => setForm(f => ({ ...f, monto: e.target.value }))}
                            size="small"
                            type="number"
                            slotProps={{
                                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                            }}
                            sx={{ flex: 2 }}
                        />
                    </Box>

                    {/* Zona de upload */}
                    <Paper
                        variant="outlined"
                        onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
                        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                        onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
                        onDrop={handleDrop}
                        sx={{
                            p: 2.5,
                            borderRadius: 1,
                            borderStyle: 'dashed',
                            borderColor: dragActive ? '#1976d2' : archivo ? '#4caf50' : '#ccc',
                            backgroundColor: dragActive ? '#e3f2fd' : archivo ? '#f1f8e9' : '#fafafa',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: '#1976d2', backgroundColor: '#f0f7ff' },
                        }}
                        component="label"
                    >
                        <input
                            type="file"
                            accept=".pdf"
                            hidden
                            onChange={e => tomarArchivo(e.target.files?.[0] || null)}
                        />
                        {archivo ? (
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                                <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 32 }} />
                                <Box textAlign="left">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {archivo.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatFileSize(archivo.size)}
                                    </Typography>
                                </Box>
                                <Chip
                                    label="Cambiar"
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                />
                            </Box>
                        ) : (
                            <Box>
                                <CloudUploadIcon sx={{ fontSize: 36, color: dragActive ? '#1976d2' : '#bbb', mb: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {dragActive ? 'Soltá el PDF aquí' : 'Click o arrastrá el archivo PDF'}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    Máximo 50 MB
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ textTransform: 'none', borderRadius: 1, px: 3 }}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default NuevaResolucionDialog;
